import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { buildApiUrl } from '../config';

// Cache for calendar events with TTL
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const eventsCache = new Map();

const getCacheKey = (filters) => {
  return JSON.stringify(filters);
};

const isValidCache = (entry) => {
  return entry && Date.now() - entry.timestamp < CACHE_TTL;
};

/**
 * Hook for fetching and caching calendar events
 * @param {Object} filters - Filter parameters for the query
 * @param {string} filters.start - Start date ISO string
 * @param {string} filters.end - End date ISO string
 * @param {string} filters.chamber - 'house', 'senate', or 'all'
 * @param {string} filters.eventType - Event type filter or 'all'
 * @param {string} filters.body - Legislative body ID or 'all'
 * @param {string} filters.committee - Committee ID or 'all'
 * @returns {Object} Events data, loading state, error, and refetch function
 */
export const useCalendarEvents = (filters = {}) => {
  const {
    start = null,
    end = null,
    chamber = 'all',
    eventType = 'all',
    body = 'all',
    committee = 'all'
  } = filters;

  const [data, setData] = useState({
    events: [],
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const loadEvents = useCallback(async ({ useCache = true } = {}) => {
    const cacheKey = getCacheKey({ start, end, chamber, eventType, body, committee });
    
    // Check cache first
    if (useCache) {
      const cached = eventsCache.get(cacheKey);
      if (isValidCache(cached)) {
        setData(cached.data);
        setLoading(false);
        return;
      }
    }

    // Abort previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      if (start) params.set('start', start);
      if (end) params.set('end', end);
      if (chamber !== 'all') params.set('chamber', chamber);
      if (eventType !== 'all') params.set('event_type', eventType);
      if (body !== 'all') params.set('body', body);
      if (committee !== 'all') params.set('committee', committee);

      const url = buildApiUrl(`/api/calendar?${params.toString()}`);
      const response = await fetch(url, {
        signal: abortRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch calendar events: ${response.status}`);
      }

      const result = await response.json();
      
      const newData = {
        events: result.events || [],
        total: result.total || result.events?.length || 0
      };

      setData(newData);
      
      // Update cache
      eventsCache.set(cacheKey, {
        data: newData,
        timestamp: Date.now()
      });

    } catch (err) {
      if (err.name === 'AbortError') {
        return; // Request was cancelled
      }
      console.error('Error fetching calendar events:', err);
      setError(err.message || 'Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  }, [start, end, chamber, eventType, body, committee]);

  // Load on mount and when filters change
  useEffect(() => {
    loadEvents({ useCache: true });
    
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [loadEvents]);

  const refetch = useCallback(() => loadEvents({ useCache: false }), [loadEvents]);

  return {
    events: data.events,
    total: data.total,
    loading,
    error,
    refetch
  };
};

/**
 * Clear the calendar events cache
 */
export const clearCalendarCache = () => {
  eventsCache.clear();
};

export default useCalendarEvents;
